import * as React from "react";

const INITIAL_DELAY = 500;
const INTERVAL_DELAY = 100;

/**
 * Hold-to-repeat for a stepper button, matching `@salt-ds/core`'s.
 *
 * ── THE TWO DELAYS ARE THE WHOLE DESIGN ───────────────────────────────────────────────
 *
 * The first repeat waits 500ms; every one after fires every 100ms. A single delay cannot do
 * both jobs: 100ms from the start makes an ordinary click increment two or three times, and
 * 500ms throughout makes holding the button useless for covering any distance.
 *
 * ── CANCELLING ON FOUR EVENTS IS NOT BELT-AND-BRACES ──────────────────────────────────
 *
 * `pointerup` alone leaves the value climbing forever in the ordinary cases: the pointer is
 * released outside the button (`pointercancel`), the window loses focus mid-press (`blur`), or
 * a right-click opens the context menu over it (`contextmenu`). Each of those ends the press
 * without a `pointerup` on the element, and each would otherwise leave the interval running.
 *
 * They are bound to the WINDOW rather than the button for the same reason — a pointer that has
 * already left the button never sends it another event.
 */
export function useLongPressPointerAction(
  actionFn: (event?: React.PointerEvent | null, block?: boolean) => void,
  isAtLimit: boolean,
): (event: React.PointerEvent) => void {
  const [active, setActive] = React.useState(false);
  const [delay, setDelay] = React.useState(INITIAL_DELAY);

  const cancel = React.useCallback(() => {
    setActive(false);
    setDelay(INITIAL_DELAY);
  }, []);

  // Stops at the boundary rather than spinning against it: holding at max should not keep
  // firing an action that can no longer change anything.
  React.useEffect(() => {
    if (isAtLimit) cancel();
  }, [isAtLimit, cancel]);

  React.useEffect(() => {
    window.addEventListener("pointerup", cancel);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("blur", cancel);
    window.addEventListener("contextmenu", cancel);
    return () => {
      window.removeEventListener("pointerup", cancel);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("blur", cancel);
      window.removeEventListener("contextmenu", cancel);
    };
  }, [cancel]);

  const activate = React.useCallback(
    (event: React.PointerEvent) => {
      // Primary mouse button, touch or pen only — a right-click or a middle-click on a stepper
      // should not start a repeat the user cannot see themselves having started.
      if (
        (event.pointerType === "mouse" && event.button === 0) ||
        event.pointerType === "touch" ||
        event.pointerType === "pen"
      ) {
        actionFn(event);
        setActive(true);
      }
    },
    [actionFn],
  );

  const savedAction = React.useRef(actionFn);
  savedAction.current = actionFn;

  React.useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      savedAction.current();
      // After the first repeat, switch to the fast cadence.
      if (delay === INITIAL_DELAY) setDelay(INTERVAL_DELAY);
    }, delay);
    return () => clearInterval(id);
  }, [active, delay]);

  return activate;
}
